<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';
import { useTrack } from 'dashboard/composables';
import { FUNNEL_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const photoInputRef = ref(null);
const selectedPhoto = ref(null);
const selectedPhotoPreview = ref('');

const formatMessagesForApi = () => {
  return messages.value.map(message => ({
    role: message.sender,
    content: message.content,
  }));
};

const removePhoto = () => {
  if (selectedPhotoPreview.value)
    URL.revokeObjectURL(selectedPhotoPreview.value);
  selectedPhoto.value = null;
  selectedPhotoPreview.value = '';
};

const resetConversation = () => {
  messages.value.forEach(msg => {
    if (msg.photoUrl) URL.revokeObjectURL(msg.photoUrl);
  });
  messages.value = [];
  newMessage.value = '';
  removePhoto();
};

const handlePhotoSelect = event => {
  const file = event.target.files[0];
  if (!file) return;
  if (selectedPhotoPreview.value)
    URL.revokeObjectURL(selectedPhotoPreview.value);
  selectedPhoto.value = file;
  selectedPhotoPreview.value = URL.createObjectURL(file);
  event.target.value = '';
};

const sendMessage = async () => {
  if ((!newMessage.value.trim() && !selectedPhoto.value) || isLoading.value)
    return;

  const userMessage = {
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date().toISOString(),
    photoUrl: selectedPhotoPreview.value || null,
  };
  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';
  // Clear refs without revoking — URL is now owned by the message bubble
  selectedPhoto.value = null;
  selectedPhotoPreview.value = '';

  try {
    isLoading.value = true;
    const { data } = await CaptainAssistant.playground({
      assistantId,
      messageContent: currentMessage,
      messageHistory: formatMessagesForApi(),
    });

    messages.value.push({
      content: data.response,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
    });

    useTrack(FUNNEL_EVENTS.PLAYGROUND_MESSAGE_SENT, {
      assistant_id: assistantId,
      message_count: messages.value.length,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting assistant response:', error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-lg p-4 border border-n-slate-4 text-n-slate-11"
  >
    <div class="mb-4">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          size="small"
          icon="i-lucide-rotate-ccw"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList :messages="messages" :is-loading="isLoading" />

    <div class="bg-n-solid-1 outline outline-n-container rounded-lg">
      <div
        v-if="selectedPhotoPreview"
        class="flex items-center gap-2 px-3 pt-2"
      >
        <div class="relative">
          <img
            :src="selectedPhotoPreview"
            class="h-16 w-16 rounded-lg object-cover"
          />
          <NextButton
            ghost
            size="xs"
            icon="i-lucide-x"
            class="absolute -top-1.5 -right-1.5 bg-n-solid-3 rounded-full !h-5 !w-5"
            @click="removePhoto"
          />
        </div>
      </div>

      <div class="relative flex items-center">
        <input
          ref="photoInputRef"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handlePhotoSelect"
        />
        <NextButton
          ghost
          size="xs"
          icon="i-lucide-image"
          class="absolute left-2 text-n-slate-11"
          @click="() => photoInputRef?.click()"
        />
        <input
          v-model="newMessage"
          class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 py-3 pl-10 pr-10"
          :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
          @keyup.enter="sendMessage"
        />
        <NextButton
          ghost
          size="xs"
          :disabled="!newMessage.trim() && !selectedPhoto"
          icon="i-lucide-send-horizontal"
          class="absolute right-2 text-n-brand"
          @click="sendMessage"
        />
      </div>
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('CAPTAIN.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
